import { useCallback, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { OutgoingCallsRequest } from '../../../../../store/reducers/OutgoingCallsSlice';
import { ReportRequest } from '../../../../../store/reducers/ReportSlice';
import { useOutgoingFilterChanges } from '../useOutgoingFilterChanges/useOutgoingFilterChanges';

interface Props {
  onCallRequest: (value: OutgoingCallsRequest) => void;
  callRequest: OutgoingCallsRequest;
  onCallRequestReport: (value: ReportRequest) => void;
  callRequestReport: ReportRequest;
}

const hasActiveFilterValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
};

type AttemptTimeRangeValue = [Dayjs, Dayjs] | null;

const toAttemptTimeRangeValue = (
  attemptFrom?: string,
  attemptTo?: string,
): AttemptTimeRangeValue => {
  if (!attemptFrom || !attemptTo) return null;

  return [dayjs(`1970-01-01T${attemptFrom}`), dayjs(`1970-01-01T${attemptTo}`)];
};

export const useOutgoingHandlersFilter = ({
  onCallRequest,
  callRequest,
  onCallRequestReport,
  callRequestReport,
}: Props) => {
  const {
    handleResult,
    handleClinics,
    handleSpecialization,
    handleDoctors,
    handleNotificationType,
    handleTaskStatus,
    handleAttemptStatus,
    handleAttemptTimeRange,
  } = useOutgoingFilterChanges({
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  });

  const [clinicValue, setClinicValue] = useState<string[]>([]);

  const handleClinicsChange = (value: string[]) => {
    setClinicValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleClinics(value);
    }
  };

  const [resultValue, setResultValue] = useState<string[]>([]);
  const [taskStatusValue, setTaskStatusValue] = useState<string[]>([]);
  const [attemptStatusValue, setAttemptStatusValue] = useState<string[]>([]);
  const [attemptTimeRangeValue, setAttemptTimeRangeValue] =
    useState<AttemptTimeRangeValue>(() =>
      toAttemptTimeRangeValue(
        callRequest['Filter.AttemptFrom'],
        callRequest['Filter.AttemptTo'],
      ),
    );

  const handleResultChange = (value: string[]) => {
    setResultValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleResult(value);
    }
  };

  const handleTaskStatusChange = (value: string[]) => {
    setTaskStatusValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleTaskStatus(value);
    }
  };

  const handleAttemptStatusChange = (value: string[]) => {
    setAttemptStatusValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleAttemptStatus(value);
    }
  };

  const handleAttemptTimeRangeChange = useCallback(
    (value: readonly [Dayjs, Dayjs] | null) => {
      const nextValue: AttemptTimeRangeValue = value
        ? [value[0], value[1]]
        : null;
      setAttemptTimeRangeValue(nextValue);
      handleAttemptTimeRange(
        nextValue?.[0].format('HH:mm:ss'),
        nextValue?.[1].format('HH:mm:ss'),
      );
    },
    [handleAttemptTimeRange],
  );

  const [specializationValue, setSpecializationValue] = useState<string[]>([]);

  const handleSpecializationChange = (value: string[]) => {
    setSpecializationValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleSpecialization(value);
    }
  };

  const [doctorsValue, setDoctorsValue] = useState<string[]>([]);

  const handleDoctorsChange = (value: string[]) => {
    setDoctorsValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleDoctors(value);
    }
  };

  const [notificationTypeValue, setNotificationTypeValue] = useState<string[]>(
    callRequest['Filter.Types'] ?? callRequest['Filter.NotificationTypes'],
  );

  const isResetDisabled = useMemo(
    () =>
      ![
        clinicValue,
        resultValue,
        taskStatusValue,
        attemptStatusValue,
        specializationValue,
        doctorsValue,
        notificationTypeValue,
        attemptTimeRangeValue,
        callRequest['Filter.ClinicIds'],
        callRequest['Filter.Result'],
        callRequest['Filter.States'],
        callRequest['Filter.AttemptStates'],
        callRequest['Filter.AttemptFrom'],
        callRequest['Filter.AttemptTo'],
        callRequest['Filter.SpecializationIds'],
        callRequest['Filter.DoctorIds'],
        callRequest['Filter.NotificationTypes'],
        callRequest['Filter.Types'],
        callRequest['Filter.FormTemplateIds'],
        callRequest['Filter.FormBatchIds'],
        callRequest['Filter.DispensarizationBatchIds'],
      ].some(hasActiveFilterValue),
    [
      clinicValue,
      resultValue,
      taskStatusValue,
      attemptStatusValue,
      specializationValue,
      doctorsValue,
      notificationTypeValue,
      attemptTimeRangeValue,
      callRequest,
    ],
  );

  const handleNotificationTypeChange = (value: string[]) => {
    setNotificationTypeValue(value);
    if (Array.isArray(value) && value.length === 0) {
      handleNotificationType(value);
    }
  };

  const handleOpenClinics = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClinics(clinicValue);
      }
    },
    [handleClinics, clinicValue],
  );

  const handleOpenResult = useCallback(
    (open: boolean) => {
      if (!open) {
        handleResult(resultValue);
      }
    },
    [handleResult, resultValue],
  );

  const handleOpenTaskStatus = useCallback(
    (open: boolean) => {
      if (!open) {
        handleTaskStatus(taskStatusValue);
      }
    },
    [handleTaskStatus, taskStatusValue],
  );

  const handleOpenAttemptStatus = useCallback(
    (open: boolean) => {
      if (!open) {
        handleAttemptStatus(attemptStatusValue);
      }
    },
    [handleAttemptStatus, attemptStatusValue],
  );

  const handleOpenSpecialization = useCallback(
    (open: boolean) => {
      if (!open) {
        handleSpecialization(specializationValue);
      }
    },
    [handleSpecialization, specializationValue],
  );

  const handleOpenDoctors = useCallback(
    (open: boolean) => {
      if (!open) {
        handleDoctors(doctorsValue);
      }
    },
    [handleDoctors, doctorsValue],
  );

  const handleOpenNotificationType = useCallback(
    (open: boolean) => {
      if (!open) {
        handleNotificationType(notificationTypeValue);
      }
    },
    [handleNotificationType, notificationTypeValue],
  );

  const resetFilters = useCallback(() => {
    if (isResetDisabled) return;

    setClinicValue([]);
    setResultValue([]);
    setTaskStatusValue([]);
    setAttemptStatusValue([]);
    setSpecializationValue([]);
    setDoctorsValue([]);
    setNotificationTypeValue([]);
    setAttemptTimeRangeValue(null);

    const nextCallRequest: OutgoingCallsRequest = {
      ...callRequest,
      PageIndex: 0,
      'Filter.ClinicIds': [],
      'Filter.Result': [],
      'Filter.States': [],
      'Filter.AttemptStates': [],
      'Filter.SpecializationIds': [],
      'Filter.DoctorIds': [],
      'Filter.NotificationTypes': [],
      'Filter.AttemptFrom': undefined,
      'Filter.AttemptTo': undefined,
      'Filter.FormTemplateIds': [],
      'Filter.FormBatchIds': [],
      'Filter.DispensarizationBatchIds': [],
    };

    if (callRequest['Filter.Types']) {
      nextCallRequest['Filter.Types'] = [];
    }

    onCallRequest(nextCallRequest);

    onCallRequestReport({
      ...callRequestReport,
      ClinicIds: [],
      Results: [],
      States: [],
      AttemptStates: [],
      SpecializationIds: [],
      DoctorIds: [],
      NotificationTypes: [],
      AttemptFrom: undefined,
      AttemptTo: undefined,
      FormTemplateIds: [],
      FormBatchIds: [],
      DispensarizationBatchIds: [],
      Types: [],
    });
  }, [
    isResetDisabled,
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  ]);

  return {
    handleClinicsChange,
    handleResultChange,
    handleTaskStatusChange,
    handleAttemptStatusChange,
    handleSpecializationChange,
    handleDoctorsChange,
    handleNotificationTypeChange,
    clinicValue,
    resultValue,
    taskStatusValue,
    attemptStatusValue,
    specializationValue,
    doctorsValue,
    notificationTypeValue,
    attemptTimeRangeValue,
    handleAttemptTimeRangeChange,
    isResetDisabled,
    handleOpenClinics,
    handleOpenResult,
    handleOpenTaskStatus,
    handleOpenAttemptStatus,
    handleOpenSpecialization,
    handleOpenDoctors,
    handleOpenNotificationType,
    resetFilters,
  };
};
